import axios, { ResponseType } from "axios";
import queryString from "query-string";
import { HTTP_TIMEOUT_DURATION } from "@root/constants/auth";
import { SessionStorageService } from "../session-storage";
import { LocalStorageService } from "../local-storage";
import "./interceptors";
import { store } from "@root/store";
import { getCurrentLanguage } from "@root/store/selectors/features";

export interface IHttpRequestOptions {
  headers?: Record<string, any>;
  responseType?: string;
}

const sessionStorageService = new SessionStorageService();
const localStorageService = new LocalStorageService();
export class HttpService {
  getTimeOutDuration() {
    return HTTP_TIMEOUT_DURATION;
  }

  async getHeaders(
    options?: IHttpRequestOptions,
    removeToken?: boolean
  ): Promise<Record<string, string>> {
    let headers: Record<string, string> = {};

    // Get current role from sessionStorage (tab-specific)
    const currentRole = sessionStorageService.getCurrentRole();
    let token: string | null = null;

    if (currentRole) {
      // Get token for current role from localStorage (shared across tabs)
      token = await localStorageService.fetchToken(currentRole);
    }

    // Fallback to old authToken key for backward compatibility
    if (!token) {
      token = await localStorageService.fetch("authToken");
    }

    const state = store.getState();
    const language = getCurrentLanguage(state)?.toLowerCase() || "ko";

    if (options && options?.headers && !options?.headers?.removeLanguage) {
      const { headers: customHeaders } = options;
      headers = customHeaders;
    }

    if (
      token &&
      typeof token === "string" &&
      !headers.Authorization &&
      !removeToken
    ) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    if (!options?.headers?.removeLanguage) {
      if (language) headers.language = language.toLowerCase();
    }

    return headers;
  }

  async get(
    url: string,
    queryParams: Record<string, any> | null = null,
    options?: IHttpRequestOptions,
    timeOut?: number
  ): Promise<any> {
    const headers: Record<string, string> = await this.getHeaders(options);
    return axios.get(url, {
      params: queryParams,
      withCredentials: true,
      paramsSerializer: function (params) {
        return queryString.stringify(params);
      },
      headers,
      timeout: timeOut ? timeOut : this.getTimeOutDuration(),
      responseType: options?.responseType as ResponseType, // <-- Forward responseType with correct type
    });
  }

  async post(
    url: string,
    postData: unknown,
    options?: IHttpRequestOptions,
    timeOut?: number
  ): Promise<unknown> {
    const headers: Record<string, string> = await this.getHeaders(options);
    if (postData instanceof FormData) {
      delete headers["Content-Type"];
    }
    return axios.post(url, postData, {
      headers,
      withCredentials: true,
      timeout: timeOut ? timeOut : this.getTimeOutDuration(),
    });
  }

  async put(
    url: string,
    postData: unknown,
    queryParams: Record<string, any> | null = null,
    options?: IHttpRequestOptions,
    removeToken?: boolean,
    timeOut?: number
  ): Promise<unknown> {
    const headers: Record<string, string> = await this.getHeaders(
      options,
      removeToken
    );

    if (postData instanceof FormData) {
      delete headers["Content-Type"];
    }

    return axios.put(url, postData, {
      params: queryParams,
      withCredentials: true,
      paramsSerializer: function (params) {
        return queryString.stringify(params);
      },
      headers,
      timeout: timeOut ? timeOut : this.getTimeOutDuration(),
    });
  }

  async patch(
    url: string,
    postData: unknown,
    options?: IHttpRequestOptions,
    timeOut?: number
  ): Promise<unknown> {
    const headers: Record<string, string> = await this.getHeaders(options);

    if (postData instanceof FormData) {
      delete headers["Content-Type"];
    }

    return axios.patch(url, postData, {
      headers,
      withCredentials: true,
      timeout: timeOut ? timeOut : this.getTimeOutDuration(),
    });
  }

  async delete(
    url: string,
    options?: IHttpRequestOptions,
    timeOut?: number,
    data?: TObject
  ): Promise<unknown> {
    const headers: Record<string, string> = await this.getHeaders(options);

    return axios.delete(url, {
      data,
      headers,
      withCredentials: true,
      timeout: timeOut ? timeOut : this.getTimeOutDuration(),
    });
  }
}
